import React from 'react';
import { Card } from '../ui/card';
import { CheckCircle2, XCircle, Check, X } from 'lucide-react';

export default function DosAndDonts() {
  const guidelines = [
    {
      category: 'Logo Usage',
      dos: [
        'Use the primary logo on white or light backgrounds',
        'Maintain minimum clear space equal to the height of the letter J',
        'Use approved color variations only',
        'Keep logo proportions intact'
      ],
      donts: [
        'Stretch, distort, or rotate the logo',
        'Use unapproved colors or gradients',
        'Add effects, shadows, or outlines',
        'Place logo on busy backgrounds'
      ]
    },
    {
      category: 'Color Application',
      dos: [
        'Use Jackstone Blue as the dominant brand color',
        'Ensure sufficient contrast for readability',
        'Follow the 60-30-10 rule for color balance',
        'Test colors for accessibility compliance'
      ],
      donts: [
        'Mix unapproved color combinations',
        'Use low-contrast color pairings',
        'Overuse accent colors',
        'Create gradients with non-brand colors'
      ]
    },
    {
      category: 'Typography',
      dos: [
        'Use Bebas Neue for H1/display headings',
        'Use Montserrat Semibold for H2-H4 headings',
        'Use Montserrat Medium for body text',
        'Maintain consistent type hierarchy'
      ],
      donts: [
        'Use decorative or script fonts',
        'Mix too many font weights',
        'Set body text smaller than 14px',
        'Use all caps for long text passages'
      ]
    },
    {
      category: 'Imagery',
      dos: [
        'Use high-quality product photography',
        'Show real manufacturing processes',
        'Maintain consistent lighting and style',
        'Include technical details and precision'
      ],
      donts: [
        'Use generic stock photography',
        'Apply heavy filters or effects',
        'Use low-resolution images',
        'Show competitors products or branding'
      ]
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Do's and Don'ts
        </h1>
        <p className="text-lg text-body leading-relaxed">
          Clear visual guidelines for correct and incorrect brand application across all touchpoints.
        </p>
      </div>

      <div className="space-y-6">
        {guidelines.map((guideline, index) => (
          <Card key={index} className="p-6">
            <h2 className="text-2xl font-heading font-semibold text-body mb-6">
              {guideline.category}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="font-display font-normal text-body flex items-center gap-2 mb-4">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                  Do This
                </h3>
                <div className="space-y-3">
                  {guideline.dos.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-success/5 border border-success/20 rounded-lg">
                      <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-body">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-display font-normal text-body flex items-center gap-2 mb-4">
                  <XCircle className="h-5 w-5 text-destructive" />
                  Don't Do This
                </h3>
                <div className="space-y-3">
                  {guideline.donts.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
                      <X className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-body">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-6 bg-light-blue border-jackstone-blue/20">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          General Best Practices
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-body mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-success" />
              Always
            </h4>
            <ul className="space-y-2 text-sm text-subtle">
              <li>• Maintain consistency across all brand touchpoints</li>
              <li>• Prioritize clarity and readability</li>
              <li>• Use approved brand assets from the official library</li>
              <li>• Follow accessibility guidelines (WCAG AA minimum)</li>
              <li>• Seek approval for new creative applications</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-body mb-3 flex items-center gap-2">
              <XCircle className="h-4 w-4 text-destructive" />
              Never
            </h4>
            <ul className="space-y-2 text-subtle">
              <li>• Recreate logo or brand elements from scratch</li>
              <li>• Deviate from established color palette</li>
              <li>• Use outdated brand materials</li>
              <li>• Apply personal design preferences over guidelines</li>
              <li>• Compromise on quality for speed</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-body mb-3">
          When In Doubt
        </h3>
        <p className="text-body mb-4">
          If you're unsure about a brand application or need clarification on these guidelines:
        </p>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-jackstone-blue rounded-full"></div>
            <p className="text-body">Contact the marketing team for guidance</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-jackstone-blue rounded-full"></div>
            <p className="text-body">Review existing approved materials for reference</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-jackstone-blue rounded-full"></div>
            <p className="text-body">Request brand asset files from the official library</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-jackstone-blue rounded-full"></div>
            <p className="text-body">Submit work for review before final production</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
